import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { FormationObligatoireReglementaire } from '../models/formation.model';
import { FormationCreateRequest } from '../requests/formation-create-request.model';
import { FormationUpdateRequest } from '../requests/formation-update-request.model';
import { FormationObligatoireRèglementaireService } from '../services/for.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';

export function avecMéthodesFormations<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<FormationObligatoireReglementaire>>(),
      props: type<EntityProps<FormationObligatoireReglementaire>>(),
    },
    withMethods((store) => ({
      chargerFormations: (state: { formations: FormationObligatoireReglementaire[] }) =>
        patchState(store, setAllEntities(state.formations)),
      ajouterFormation: (state: { formation: FormationObligatoireReglementaire }) =>
        patchState(store, upsertEntity(state.formation)),
      modifierFormation: (state: { id: string; formation: Partial<FormationObligatoireReglementaire> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.formation })),
      supprimerFormation: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(FormationObligatoireRèglementaireService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            service.trouverTous(idCartographie).pipe(
              tapResponse({
                next: (result) => store.chargerFormations({ formations: result.items }),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des formations règlementaires, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        nouvelleFormation: FormationCreateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ nouvelleFormation, next }) =>
            service.ajouter(nouvelleFormation).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de l'ajout d'une formation règlementaire, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  return console.error("Error lors de l'ajout d'une formation : ", err);
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: FormationUpdateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.modifier(id, data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error("Error lors de la modification d'une formation : ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification d'une formation règlementaire, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{
        id: string;
        next: () => void;
      }>(
        pipe(
          switchMap(({ id, next }) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => {
                  store.supprimerFormation(id);
                  next();
                },
                error: (err) => {
                  console.error("Error lors de la suppression d'une formation : ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression d'une formation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
